import { useState, useCallback, useRef, useEffect } from 'react';
import type { PrivateResearchItem } from './types';

interface UsePrivateResearchLightboxProps {
  allResearch: PrivateResearchItem[];
}

export const usePrivateResearchLightbox = ({
  allResearch,
}: UsePrivateResearchLightboxProps) => {
  const [selectedResearch, setSelectedResearch] = useState<PrivateResearchItem | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isGeneratingRelatedQuestion, setIsGeneratingRelatedQuestion] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Research click handler
  const handleResearchClick = useCallback((researchIdOrItem: string | PrivateResearchItem) => {
    let targetResearch: PrivateResearchItem | undefined;
    
    if (typeof researchIdOrItem === 'string') {
      targetResearch = allResearch.find(r => r.id === researchIdOrItem);
    } else {
      targetResearch = researchIdOrItem;
    }
    
    if (targetResearch) {
      setSelectedResearch(targetResearch);
      setIsLightboxOpen(true);
    }
  }, [allResearch]);

  // Close lightbox handler
  const handleCloseLightbox = useCallback(() => {
    setIsLightboxOpen(false);
    setSelectedResearch(null);
  }, []);

  // Navigation handlers for lightbox
  const currentIndex = selectedResearch ? allResearch.findIndex(r => r.id === selectedResearch.id) : -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < allResearch.length - 1;

  const handlePreviousResearch = useCallback(() => {
    if (hasPrevious && currentIndex > 0) {
      const prevResearch = allResearch[currentIndex - 1];
      setSelectedResearch(prevResearch);
    }
  }, [hasPrevious, currentIndex, allResearch]);

  const handleNextResearch = useCallback(() => {
    if (hasNext && currentIndex < allResearch.length - 1) {
      const nextResearch = allResearch[currentIndex + 1];
      setSelectedResearch(nextResearch);
    }
  }, [hasNext, currentIndex, allResearch]);

  // Handle generation state changes with 60-second timeout
  const handleGenerationStateChange = useCallback((isGenerating: boolean) => {
    console.log('[PrivateResearchLightbox] Generation state changed:', isGenerating);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsGeneratingRelatedQuestion(isGenerating);

    if (isGenerating) {
      // Set 60-second timeout as safety mechanism
      timeoutRef.current = setTimeout(() => {
        console.log('[PrivateResearchLightbox] 60-second timeout reached, resetting generation state');
        setIsGeneratingRelatedQuestion(false);
        timeoutRef.current = null;
      }, 60000);
    }
  }, []);

  // Listen for custom events to open research detail
  useEffect(() => {
    const handleOpenResearchEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const researchId = customEvent.detail?.researchId;
      
      console.log('[PrivateResearchLightbox] Received open-research-detail event:', researchId);
      console.log('[PrivateResearchLightbox] Currently loaded research:', {
        count: allResearch.length,
        researchIds: allResearch.map(r => r.id).slice(0, 5) // Show first 5 IDs
      });
      
      if (researchId && allResearch.length > 0) {
        const targetResearch = allResearch.find(r => r.id === researchId);
        if (targetResearch) {
          console.log('[PrivateResearchLightbox] Opening lightbox for research via event:', researchId);
          setSelectedResearch(targetResearch);
          setIsLightboxOpen(true);
        } else {
          console.log('[PrivateResearchLightbox] Research not found in loaded research:', researchId);
          console.log('[PrivateResearchLightbox] Available research IDs:', allResearch.map(r => r.id));
        }
      }
    };

    window.addEventListener('open-research-detail', handleOpenResearchEvent);
    
    return () => {
      window.removeEventListener('open-research-detail', handleOpenResearchEvent);
    };
  }, [allResearch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle toggle favorite (placeholder - private research doesn't have favorites yet)
  const handleToggleFavorite = useCallback(async (researchId: string) => {
    console.log('Toggle favorite not implemented for private research:', researchId);
  }, []);

  return {
    selectedResearch,
    isLightboxOpen,
    isGeneratingRelatedQuestion,
    currentIndex,
    hasPrevious,
    hasNext,
    handleResearchClick,
    handleCloseLightbox,
    handlePreviousResearch,
    handleNextResearch,
    handleGenerationStateChange,
    handleToggleFavorite,
  };
}; 